import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaHouse,
  FaMagnifyingGlass,
  FaBagShopping,
  FaPlus,
  FaShop,
  FaCalendarDays,
  FaTag,
  FaXmark,
} from "react-icons/fa6";
import { getCurrentUser } from "./session";
import SignInModal from "./SignInModal";
import "./BottomTabBar.css";

/* =========================================================
   GoOurHub bottom tab bar   mobile only (≤980px)
   Home · Search · HubMart · Add

   "Add" is the ONE predictable place to create content. It opens
   a bottom sheet with every add action, so the page itself can
   stop scattering "Post a deal / Add business / Add Biz" buttons.

   ROUTES: adjust the paths in TABS and ADD_OPTIONS if yours differ.
   ========================================================= */
const TABS = [
  { key: "home",    label: "Home",    path: "/",        icon: FaHouse },
  { key: "search",  label: "Search",  path: "/",        icon: FaMagnifyingGlass, isSearch: true },
  { key: "hubmart", label: "HubMart", path: "/HubMart", icon: FaBagShopping },
  { key: "add",     label: "Add",     path: null,       icon: FaPlus,       isAdd: true },
];

const HUB_PATHS = ["/", "/hub"];

const ADD_OPTIONS = [
  {
    key: "business",
    icon: FaShop,
    label: "List a business",
    hint: "Free listing in the directory",
    action: "route",
    path: "/partner-signup",
  },
  {
    key: "event",
    icon: FaCalendarDays,
    label: "Post an event",
    hint: "Community events, free",
    /* HubQuickActions on the hub listens for this and opens the
       PostEventSheet (sign-in gated). From other pages we go to the
       hub first, then fire it once the hub has mounted. */
    action: "post-event",
  },
  {
    key: "deal",
    icon: FaTag,
    label: "Post a deal",
    hint: "An offer from your business",
    action: "post-offer",
    path: "/post-offer",
  },
  {
    key: "hubmart",
    icon: FaBagShopping,
    label: "Sell on HubMart",
    hint: "Cars, phones, furniture, anything",
    action: "route",
    path: "/HubMart",
  },
];

function BottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  const pathname = location.pathname.toLowerCase();

  /* route change closes the sheet */
  useEffect(() => {
    setAddOpen(false);
  }, [location.pathname]);

  /* Esc closes; body scroll locks while the sheet is up */
  useEffect(() => {
    if (!addOpen) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") setAddOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [addOpen]);

  const focusHeaderSearch = () => {
    // Try to focus the header's search input; retry briefly if
    // we just navigated and the header hasn't rendered yet.
    let attempts = 0;
    const tryFocus = () => {
      const input = document.querySelector(".header-search-input");
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (attempts < 10) {
        attempts += 1;
        setTimeout(tryFocus, 100);
      }
    };
    tryFocus();
  };

  const firePostEvent = () => {
    const fire = () => window.dispatchEvent(new CustomEvent("gohub:post-event"));

    if (HUB_PATHS.includes(pathname)) {
      fire();
      return;
    }

    // Not on the hub: go there, then fire once its listener exists.
    navigate("/hub");
    [200, 600, 1200].forEach((delay) => setTimeout(fire, delay));
  };

  const runAddOption = (option) => {
    setAddOpen(false);

    if (option.action === "post-event") {
      firePostEvent();
      return;
    }

    if (option.action === "post-offer") {
      const user = getCurrentUser();
      if (user?.email && user?.token) {
        navigate(option.path);
      } else {
        setSignInOpen(true);
      }
      return;
    }

    navigate(option.path);
  };

  const handleTab = (tab) => {
    if (tab.isAdd) {
      setAddOpen((value) => !value);
      return;
    }

    setAddOpen(false);

    if (tab.isSearch) {
      if (location.pathname !== tab.path) navigate(tab.path);
      window.scrollTo({ top: 0, behavior: "smooth" });
      focusHeaderSearch();
      return;
    }

    if (location.pathname !== tab.path) {
      navigate(tab.path);
      window.scrollTo({ top: 0 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isActive = (tab) => {
    if (tab.isSearch) return false; // search is an action, not a place
    if (tab.isAdd) return addOpen;
    if (tab.path === "/") return pathname === "/";
    return pathname.startsWith(tab.path.toLowerCase());
  };

  const addSheet =
    addOpen &&
    createPortal(
      <div className="goh-addsheet-wrap" onClick={() => setAddOpen(false)}>
        <div
          className="goh-addsheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="goh-addsheet-title"
          onClick={(event) => event.stopPropagation()}
        >
          <span className="goh-addsheet__handle" aria-hidden="true" />

          <div className="goh-addsheet__top">
            <h2 id="goh-addsheet-title">What would you like to add?</h2>
            <button
              type="button"
              className="goh-addsheet__close"
              onClick={() => setAddOpen(false)}
              aria-label="Close"
            >
              <FaXmark />
            </button>
          </div>

          <div className="goh-addsheet__list">
            {ADD_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.key}
                  type="button"
                  className={`goh-addsheet__row goh-addsheet__row--${option.key}`}
                  onClick={() => runAddOption(option)}
                >
                  <span className="goh-addsheet__ic" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="goh-addsheet__tx">
                    <strong>{option.label}</strong>
                    <small>{option.hint}</small>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <nav className="goh-tabbar" aria-label="Primary">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab);
          return (
            <button
              key={tab.key}
              type="button"
              className={`goh-tabbar__tab${active ? " is-active" : ""}${
                tab.isAdd ? " goh-tabbar__tab--add" : ""
              }`}
              aria-current={active && !tab.isAdd ? "page" : undefined}
              aria-expanded={tab.isAdd ? addOpen : undefined}
              aria-haspopup={tab.isAdd ? "dialog" : undefined}
              onClick={() => handleTab(tab)}
            >
              <span className="goh-tabbar__icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="goh-tabbar__label">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {addSheet}

      {signInOpen && (
        <SignInModal
          isOpen={signInOpen}
          onClose={() => setSignInOpen(false)}
          onSignInSuccess={() => {
            setSignInOpen(false);
            navigate("/post-offer");
          }}
        />
      )}
    </>
  );
}

export default BottomTabBar;
